// needsYouModel.ts — pure helpers for the "Needs you" page and its nav badge.

import type { Tone } from '../../components/tone/tone';
import type {
  AttentionApiAction,
  AttentionCounts,
  AttentionItem,
  AttentionResponse,
  AttentionSeverity,
} from '../../api/types';
import { CHANGES_REQUESTED_ANCHOR } from '../../components/merge/reviewListModel';
import { compareInstants } from '../../format/when';

/** Where "what needs a human" lives; every other surface links here. */
export const NEEDS_YOU_PATH = '/needs-you';
/** Every row, not only the viewer's: the All tab of Needs you (once `/blocked`). */
export const BLOCKED_PATH = `${NEEDS_YOU_PATH}?view=all`;

export const SEVERITIES: readonly AttentionSeverity[] = ['critical', 'action', 'watch'];

export const SEVERITY_LABEL: Record<AttentionSeverity, string> = {
  critical: 'Broken — the pipeline is stuck',
  // Blocked on a person — any person: a todo here can be claimed or handed
  // to someone from its Work page, so it is not only the viewer's.
  action: 'Blocked',
  watch: 'Worth a look',
};

export interface AttentionGroup {
  severity: AttentionSeverity;
  label: string;
  items: AttentionItem[];
}

/**
 * One subject with everything the pipeline says about it: the pull request,
 * the todo or the shift a reader thinks of as "the thing that is stuck". The
 * same cause often raises several items at once — a review hold leaves changes
 * requested, a reviewer that could not finish and a queue entry that failed —
 * and three red rows with three different acts for one pull request read as
 * three problems. They are one row here.
 */
export interface AttentionSubject {
  key: string;
  /** The item whose act is offered; see `KIND_PRECEDENCE`. */
  primary: AttentionItem;
  /** The rest of the subject's items, in the same precedence, as context. */
  also: AttentionItem[];
  /** The worst severity among the subject's items. */
  severity: AttentionSeverity;
  /** The oldest `since` among them: how long the subject has been waiting. */
  since: string | null;
}

export interface SubjectGroup {
  severity: AttentionSeverity;
  label: string;
  subjects: AttentionSubject[];
}

/**
 * What a row is about, so that every item raised by one cause lands on one
 * row: a pull request, else the todo, else the shift. An item that names none
 * of those is its own subject — its id never collides with another item's.
 */
export function subjectKey(
  item: Pick<AttentionItem, 'id' | 'repo' | 'pr' | 'family' | 'todo_id' | 'shift'>
): string {
  const repo = item.repo?.trim();
  if (repo && item.pr) return `pr:${repo}#${item.pr}`;
  const todo = item.todo_id?.trim();
  if (todo) return `todo:${familyName(item.family) ?? ''}:${todo}`;
  const shift = item.shift?.trim();
  if (shift) return `shift:${familyName(item.family) ?? ''}:${shift}`;
  return `item:${item.id}`;
}

/**
 * Which of a subject's items names the act to offer, most first. The order is
 * fixed rather than derived from severity, so one cause always resolves to the
 * same act whatever else it happened to raise.
 *
 * It reads from the act that unsticks the subject to the act that only follows
 * from it: a review that could not be finished comes before the changes it
 * left requested (nobody can push a fix for a judgement not yet made), and a
 * failed gate or queue entry comes before waiting on an approval that gate
 * would have to pass anyway. A kind this list does not name sorts last, in
 * severity order, so a new kind is context next to a known act rather than
 * silently taking it over.
 */
export const KIND_PRECEDENCE: readonly string[] = [
  // Already on its base: closing it is the only act left, whatever else it raised.
  'pr_already_landed',
  'reviewer_stuck',
  // One answer from the owner unblocks everything else this pull request
  // raised, so it is the act on the row whatever else it says.
  'pr_needs_owner_decision',
  'pr_changes_requested',
  'queue_refused',
  'queue_failed',
  'queue_stuck',
  'pr_checks_failing',
  'pr_draft_waiting',
  'pr_awaiting_approval',
  'pr_ready_to_merge',
  'todo_blocked',
  'todo_check_outcome',
  'todo_handoff',
  'todo_stuck_claim',
  'todo_waiting_on_blocker',
  'todo_untriaged',
  'todo_parked',
  'shift_stranded_work',
  'shift_without_pr',
  'shift_budget_spent',
  'release_stage_failed',
  'deploy_failed',
  'release_staged',
  'pin_behind',
  'mirror_failing',
  'mirror_diverged',
  'gate_runner_down',
  'workers_down',
];

/** A kind's place in `KIND_PRECEDENCE`; an unnamed kind sorts after them all. */
export function kindRank(kind: string): number {
  const at = KIND_PRECEDENCE.indexOf(kind);
  return at === -1 ? KIND_PRECEDENCE.length : at;
}

function severityRank(item: Pick<AttentionItem, 'severity'>): number {
  return SEVERITIES.indexOf(severityOf(item));
}

/** The precedence a subject's items are ordered by: its first item acts. */
function byPrecedence(a: AttentionItem, b: AttentionItem): number {
  return (
    kindRank(a.kind) - kindRank(b.kind) ||
    severityRank(a) - severityRank(b) ||
    compareInstants(a.since, b.since) ||
    a.id.localeCompare(b.id)
  );
}

/**
 * One row per subject, worst severity first and longest-waiting first within a
 * severity. Order among equals is the subject key, so a reload never shuffles
 * the list.
 */
export function attentionSubjects(items: AttentionItem[]): AttentionSubject[] {
  const bySubject = new Map<string, AttentionItem[]>();
  for (const item of items) {
    const key = subjectKey(item);
    const group = bySubject.get(key);
    if (group) group.push(item);
    else bySubject.set(key, [item]);
  }
  return [...bySubject]
    .map(([key, group]) => {
      const [primary, ...also] = [...group].sort(byPrecedence);
      const dated = group.map((item) => item.since).filter((since): since is string => !!since);
      return {
        key,
        primary,
        also,
        severity: SEVERITIES[Math.min(...group.map(severityRank))],
        since: dated.length > 0 ? [...dated].sort(compareInstants)[0] : null,
      };
    })
    .sort(
      (a, b) =>
        SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity) ||
        compareInstants(a.since, b.since) ||
        a.key.localeCompare(b.key)
    );
}

/** `groupAttention`, by subject: critical, then action, then watch. */
export function groupSubjects(items: AttentionItem[]): SubjectGroup[] {
  const subjects = attentionSubjects(items);
  return SEVERITIES.map((severity) => ({
    severity,
    label: SEVERITY_LABEL[severity],
    subjects: subjects.filter((subject) => subject.severity === severity),
  })).filter((group) => group.subjects.length > 0);
}

/**
 * One of a subject's other items, as the one line a row shows under its act:
 * what it is, what it would have asked for, and where to go for it.
 */
export function alsoLine(item: AttentionItem): { label: string; detail: string; to: string | null } {
  return {
    label: kindLabel(item.kind),
    detail: rowDetail(item)?.text ?? item.title,
    to: attentionHref(item),
  };
}

/** An unknown severity is shown, not dropped: it lands under `watch`. */
export function severityOf(item: Pick<AttentionItem, 'severity'>): AttentionSeverity {
  return (SEVERITIES as readonly string[]).includes(item.severity)
    ? (item.severity as AttentionSeverity)
    : 'watch';
}

/** Critical, then action, then watch; oldest first inside a group; empty groups dropped. */
export function groupAttention(items: AttentionItem[]): AttentionGroup[] {
  return SEVERITIES.map((severity) => ({
    severity,
    label: SEVERITY_LABEL[severity],
    items: items
      .filter((item) => severityOf(item) === severity)
      .sort(
        (a, b) =>
          compareInstants(a.since, b.since) || a.id.localeCompare(b.id)
      ),
  })).filter((group) => group.items.length > 0);
}

/**
 * Red is reserved for rows where a person is the next step (critical, action).
 * `watch` rows may heal on their own, so they stay neutral.
 */
export function severityTone(severity: AttentionSeverity): Tone {
  return severity === 'watch' ? 'unknown' : 'human';
}

/**
 * The nav badge: critical + action, counted from the rows themselves
 * (`urgentAttention`) rather than the server's `counts`, so the badge, the
 * dock, each page's strip and Needs you itself can never disagree.
 */
export function attentionBadgeCount(data: AttentionResponse | undefined): number {
  return urgentAttention(data).length;
}

const KIND_LABEL: Record<string, string> = {
  // The section heading already says Blocked: the row says what it is.
  todo_blocked: 'Todo',
  todo_check_outcome: 'Check outcome',
  todo_handoff: 'Todo handed to you',
  todo_stuck_claim: 'Stuck claim',
  todo_untriaged: 'Todo to triage',
  todo_waiting_on_blocker: 'Waiting on another todo',
  shift_without_pr: 'Shift has no review pull request',
  shift_budget_spent: 'Shift budget spent',
  pr_changes_requested: 'Changes requested',
  pr_needs_owner_decision: 'Needs your decision',
  pr_checks_failing: 'Checks failing',
  pr_awaiting_approval: 'Awaiting approval',
  pr_ready_to_merge: 'Ready to merge',
  pr_draft_waiting: 'Draft waiting to be marked ready',
  pr_already_landed: 'Already on the base branch',
  queue_failed: 'Merge queue failed',
  reviewer_stuck: 'Reviewer stuck',
  reviewer_rereviewing: 'Reviewing the current head',
  gate_runner_down: 'Gate runners down',
  workers_down: 'Workers down',
  release_staged: 'Release ready to deploy',
  release_stage_failed: 'Release staging failed',
  deploy_failed: 'Deploy failed',
  pin_behind: 'Merged, not pinned for release',
  stranded_branch: 'Stranded branch',
  pr_changes_own_gate: 'Changes its own gate',
  pr_branch_campaign: 'Branch campaign',
  pushed_after_merge: 'Pushed after merge',
  release_board_problem: 'Release board',
};

/** A branch with work on no main and no pull request carrying it. */
export const STRANDED_KIND = 'stranded_branch';

/** Stranded branches untouched for longer than this fold into one line. */
export const STALE_STRANDED_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The rows to list, and the stranded branches older than `days` that fold into
 * one summary line instead: dozens of three-month-old salvage branches pushed
 * everything that changed this week off the screen. A row with no `since` is
 * never folded — its age is not known.
 */
export function splitStaleStranded(
  subjects: AttentionSubject[],
  now: Date,
  days: number = STALE_STRANDED_DAYS
): { shown: AttentionSubject[]; stale: AttentionSubject[] } {
  const cutoff = now.getTime() - days * DAY_MS;
  const shown: AttentionSubject[] = [];
  const stale: AttentionSubject[] = [];
  for (const subject of subjects) {
    const at = subject.since ? Date.parse(subject.since) : Number.NaN;
    const old = subject.primary.kind === STRANDED_KIND && Number.isFinite(at) && at < cutoff;
    (old ? stale : shown).push(subject);
  }
  return { shown, stale };
}

/** A plain-language label; an unknown kind reads as a sentence, never raw. */
export function kindLabel(kind: string): string {
  const known = KIND_LABEL[kind];
  if (known) return known;
  const words = kind.replace(/[_.]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Needs attention';
}

/**
 * The row's title, without a leading "Blocked:" — the section the row sits in
 * says that already, and a todo blocked on a person was saying it three times.
 */
export function rowTitle(item: Pick<AttentionItem, 'title'>): string {
  const title = item.title.replace(/^\s*blocked\s*:\s*/i, '');
  return title || item.title;
}

/**
 * "Changes requested · jeryu/jeryu-web#35" — the muted half of the title line.
 * The family is the pill at the row's left, so it is not said again here, and
 * a repository the title already names is not repeated either.
 */
export function attentionContext(item: AttentionItem): string {
  const where = item.repo ? `${item.repo}${item.pr ? `#${item.pr}` : ''}` : '';
  const named = where !== '' && item.title.includes(where);
  return [kindLabel(item.kind), named ? '' : where].filter(Boolean).join(' · ');
}

export type ApiMethod = 'POST' | 'PATCH' | 'DELETE';

export type PrimaryAction =
  | {
      type: 'api';
      label: string;
      method: ApiMethod;
      path: string;
      body: unknown;
      confirm: string;
    }
  | { type: 'command'; label: string; command: string; where: string | null }
  | { type: 'link'; label: string; to: string }
  | null;

/**
 * Exactly one thing to do per row: the call the forge can make for you, else
 * the copyable command when the act happens off-site, else the link to where
 * to act. A row's title links to `href` either way, so a row whose act is a
 * button still leads to its subject.
 */
export function primaryAction(item: AttentionItem): PrimaryAction {
  const label = item.action?.label || 'Open';
  const api = item.action?.api;
  const path = safeApiPath(api?.path);
  const method = api ? apiMethod(api.method) : null;
  if (api && path && method) {
    return {
      type: 'api',
      label,
      method,
      path,
      body: api.body,
      confirm: api.confirm?.trim() || `${label} now? The forge acts straight away.`,
    };
  }
  if (item.action?.command) {
    return { type: 'command', label, command: item.action.command, where: commandPlace(item) };
  }
  const to = attentionHref(item);
  return to ? { type: 'link', label, to } : null;
}

/** One answer to an owner decision: the call and what the confirm step says. */
export interface DecisionAnswer {
  label: string;
  method: ApiMethod;
  path: string;
  body?: unknown;
  confirm: string;
}

export interface OwnerDecision {
  question: string;
  yes: DecisionAnswer;
  no: DecisionAnswer;
}

function answer(
  call: AttentionApiAction | undefined,
  label: string,
  fallback: string
): DecisionAnswer | null {
  const path = safeApiPath(call?.path);
  const method = call ? apiMethod(call.method) : null;
  if (!call || !path || !method) return null;
  return {
    label,
    method,
    path,
    body: call.body,
    confirm: call.confirm?.trim() || fallback,
  };
}

/**
 * The question a row asks the owner, when it asks one and both answers are
 * calls this page can make. A server that predates `decision`, or a payload
 * whose calls are not same-origin `/api/` writes, reads as no question at all:
 * the row then falls back to its link, and nobody is offered a button that
 * does not answer anything.
 */
export function ownerDecision(item: AttentionItem): OwnerDecision | null {
  const decision = item.decision;
  const question = decision?.question?.trim();
  if (!decision || !question) return null;
  const yes = answer(decision.yes, 'Yes', 'Record your Yes on this exact head?');
  const no = answer(decision.no, 'No', 'Record your No on this exact head?');
  return yes && no ? { question, yes, no } : null;
}

/**
 * Only a same-origin `/api/` path is called, including `/api/v3`, and only
 * `POST`, `PATCH`, or `DELETE` (`apiMethod`). Anything else is not an act
 * this page knows how to make, so the row falls back to its command or its
 * link rather than offering a button that lies.
 */
export function safeApiPath(path: string | null | undefined): string | null {
  const trimmed = (path ?? '').trim();
  return trimmed.startsWith('/api/') && !trimmed.startsWith('//') ? trimmed : null;
}

export function apiMethod(method: string | null | undefined): ApiMethod | null {
  const name = (method ?? 'POST').trim().toUpperCase();
  if (name === '' || name === 'POST') return 'POST';
  if (name === 'PATCH') return 'PATCH';
  return name === 'DELETE' ? 'DELETE' : null;
}

export interface RowDetail {
  text: string;
  title: string;
  /** Where the step's "open <path>" leads, shown in place of the bare path. */
  to?: string;
}

/** "Read the review: open /repos/…" — a next step that ends in a path to open. */
const OPEN_STEP = /^(.*\S)\s*:\s*open\s+(\/\S*)$/;

/**
 * The row's second line: the server's one-sentence next step when it sends
 * one, else the reason. The reason stays the tooltip either way, since it is
 * often the longer of the two.
 *
 * A step ending in ": open <path>" is not shown with the bare path in it: the
 * path becomes a link (`to`). When that step only repeats the row's own link
 * button (same words, same place), the line says the reason instead, which
 * the button does not.
 */
export function rowDetail(
  item: Pick<AttentionItem, 'next_step' | 'reason'> &
    Partial<Pick<AttentionItem, 'kind' | 'href' | 'action'>>
): RowDetail | null {
  // A stranded branch's title already names the repository, the branch, the
  // count and the newest commit, and its reason says all of them again; the
  // act ("Open a pull request for the branch") is the button.
  if (item.kind === STRANDED_KIND) return null;
  const step = (item.next_step ?? '').trim();
  const reason = (item.reason ?? '').trim();
  const opened = OPEN_STEP.exec(step);
  const path = opened ? safeHref(opened[2]) : null;
  if (opened && path) {
    const words = opened[1].trim();
    const own = path === safeHref(item.href);
    const label = (item.action?.label ?? '').trim();
    if (own && reason && words === label) return { text: reason, title: reason };
    const to = own ? attentionHref({ kind: item.kind ?? '', href: item.href ?? null }) : path;
    return { text: words, title: reason || words, to: to ?? path };
  }
  const text = step || reason;
  return text ? { text, title: reason || text } : null;
}

/**
 * Where a row's command is run, as one short line shown above it. The server
 * names the machine and directory (`action.run_in`); one that predates the
 * field leaves the item's repository as the only hint, and with neither there
 * is nothing true to say.
 */
export function commandPlace(item: Pick<AttentionItem, 'action' | 'repo'>): string | null {
  const runIn = item.action?.run_in?.trim();
  if (runIn) return `Run on ${runIn}`;
  const repo = item.repo?.trim();
  return repo ? `Run in a checkout of ${repo}` : null;
}

export interface SystemPulseInput {
  workers?: Array<{ healthy: boolean; state: string; slot: string }>;
  runners?: { onlineRunners: number; busyRunners: number };
  lastEvent?: { ts: string; summary: string };
}

/**
 * One calm line about what the system is doing, so "nothing needs you" reads
 * as alive rather than broken. Parts with no data are left out.
 */
export function systemPulse(input: SystemPulseInput, ago: (iso: string) => string): string {
  const parts: string[] = [];
  if (input.workers) {
    const slots = input.workers.filter((w) => w.healthy && w.slot !== 'supervisor');
    const busy = slots.filter((w) => w.state === 'working').length;
    parts.push(`${busy} of ${slots.length} worker slot${slots.length === 1 ? '' : 's'} busy`);
  }
  if (input.runners) {
    parts.push(`${input.runners.busyRunners} of ${input.runners.onlineRunners} gate runners busy`);
  }
  if (input.lastEvent) {
    parts.push(`last event ${ago(input.lastEvent.ts)}: ${input.lastEvent.summary}`);
  }
  return parts.join(' · ');
}

/** Only same-origin app paths are followed; anything else is not a link. */
export function safeHref(href: string | null | undefined): string | null {
  return href && href.startsWith('/') && !href.startsWith('//') ? href : null;
}

/**
 * Where a row leads. A "Changes requested" row names only the pull request,
 * so its link adds `#changes-requested`: the pull request page opens the
 * review that asks for changes and scrolls to it, instead of leaving the
 * reader to hunt for it. Every other row leads to its `href` unchanged.
 */
export function attentionHref(item: Pick<AttentionItem, 'kind' | 'href'>): string | null {
  const to = safeHref(item.href);
  if (!to || item.kind !== 'pr_changes_requested' || to.includes('#')) return to;
  return `${to}#${CHANGES_REQUESTED_ANCHOR}`;
}

/**
 * The todo a row is about, when it is about one: rows about a todo offer
 * "Acknowledge until…". Rows about a pull request, a release or the runners name no todo,
 * so they are not acknowledged from here.
 */
export function acknowledgeTarget(
  item: Pick<AttentionItem, 'family' | 'todo_id'>
): { family: string; id: string } | null {
  const family = (item.family ?? '').trim();
  const id = (item.todo_id ?? '').trim();
  return family && id ? { family, id } : null;
}

export function findAttention(
  data: AttentionResponse | undefined,
  kind: string
): AttentionItem | undefined {
  return data?.items?.find((item) => item.kind === kind);
}

/** Items no family owns: the forge itself (a staged release, the mirror). */
export const FORGE_FAMILY = 'forge';

/** `jeryu-split` and `jeryu` are one family to a reader. */
export function familyName(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  return trimmed ? trimmed.replace(/-split$/, '') : null;
}

/**
 * The family an item belongs to: what the server says, else the family of its
 * repository (`owner/name` looked up in `repoFamilies`), else the forge.
 */
export function familyOf(
  item: Pick<AttentionItem, 'family' | 'repo'>,
  repoFamilies: ReadonlyMap<string, string>
): string {
  return (
    familyName(item.family) ??
    familyName(item.repo ? repoFamilies.get(item.repo) : null) ??
    FORGE_FAMILY
  );
}

export interface FamilyCount {
  family: string;
  count: number;
}

/** Busiest first, the forge last. */
function byBusiest(a: FamilyCount, b: FamilyCount): number {
  return (
    Number(a.family === FORGE_FAMILY) - Number(b.family === FORGE_FAMILY) ||
    b.count - a.count ||
    a.family.localeCompare(b.family)
  );
}

/** Families with something waiting, busiest first, the forge last. */
export function familyCounts(
  items: Pick<AttentionItem, 'family' | 'repo'>[],
  repoFamilies: ReadonlyMap<string, string>
): FamilyCount[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const family = familyOf(item, repoFamilies);
    counts.set(family, (counts.get(family) ?? 0) + 1);
  }
  return [...counts].map(([family, count]) => ({ family, count })).sort(byBusiest);
}

/**
 * The rows waiting on a person (critical + action) in each family, from the
 * server's whole-inbox `families` summary (schema v1.5). It counts rows the
 * way this page draws them, one per pull request, todo or shift, so a pill, a
 * badge and the page header agree. Null from a server that sends none.
 */
export function summaryFamilyCounts(data: AttentionResponse | undefined): FamilyCount[] | null {
  if (!data?.families) return null;
  const counts = new Map<string, number>();
  for (const entry of data.families) {
    const family = familyName(entry.family) ?? FORGE_FAMILY;
    const waiting = entry.subjects.critical + entry.subjects.action;
    if (waiting > 0) counts.set(family, (counts.get(family) ?? 0) + waiting);
  }
  return [...counts].map(([family, count]) => ({ family, count })).sort(byBusiest);
}

/** Keep one family's items; an empty filter keeps everything. */
export function filterByFamily<T extends Pick<AttentionItem, 'family' | 'repo'>>(
  items: T[],
  family: string,
  repoFamilies: ReadonlyMap<string, string>
): T[] {
  return family ? items.filter((item) => familyOf(item, repoFamilies) === family) : items;
}

/**
 * The page where a row's cause lives, so each page can show its own share of
 * "Needs you": todos and shifts on Work, pull requests and their queue on In
 * flight, staging, deploys and pins on Releases, gate runners and workers
 * under System. A kind with no home stays on Needs you only.
 */
export type AttentionArea = 'work' | 'pulls' | 'releases' | 'system';

export const AREA_LABEL: Record<AttentionArea, string> = {
  work: 'Work',
  pulls: 'In flight',
  releases: 'Releases',
  system: 'Runners',
};

const AREA_BY_PREFIX: ReadonlyArray<readonly [string, AttentionArea]> = [
  ['todo_', 'work'],
  ['shift_', 'work'],
  ['pr_', 'pulls'],
  ['queue_', 'pulls'],
  ['reviewer_', 'pulls'],
  ['release_', 'releases'],
  ['deploy_', 'releases'],
  ['pin_', 'releases'],
  ['gate_runner', 'system'],
  ['workers_', 'system'],
];

export function attentionArea(kind: string): AttentionArea | null {
  const hit = AREA_BY_PREFIX.find(([prefix]) => kind.startsWith(prefix));
  return hit ? hit[1] : null;
}

/** The rows waiting on a person (critical, then action) whose cause lives in `area`. */
export function urgentInArea(
  data: AttentionResponse | undefined,
  area: AttentionArea
): AttentionItem[] {
  const items = (data?.items ?? []).filter(
    (item) => severityOf(item) !== 'watch' && attentionArea(item.kind) === area
  );
  return groupAttention(items).flatMap((group) => group.items);
}

/** The nav badge for one page: its critical + action rows. */
export function areaBadgeCount(data: AttentionResponse | undefined, area: AttentionArea): number {
  return urgentInArea(data, area).length;
}

/**
 * The family a badge counts for, and the map that places an item's repository
 * in a family. Absent — or an empty family — counts every family.
 */
export interface AttentionScope {
  family: string;
  repoFamilies: ReadonlyMap<string, string>;
}

/**
 * What a badge shows and what it is leaving out: the rows in scope, and how
 * many wait in other families. Nothing urgent is hidden by a scope — the count
 * narrows with the page it leads to, and the rest is said beside it.
 */
export interface BadgeCount {
  count: number;
  elsewhere: number;
}

function scopedCount(
  items: AttentionItem[],
  scope: AttentionScope | undefined
): BadgeCount {
  if (!scope?.family) return { count: items.length, elsewhere: 0 };
  const inScope = filterByFamily(items, scope.family, scope.repoFamilies);
  return { count: inScope.length, elsewhere: items.length - inScope.length };
}

/**
 * The "Needs you" badge, for the family in scope: the server's per-family
 * row counts when it sends them, else the urgent items in hand.
 */
export function attentionBadge(
  data: AttentionResponse | undefined,
  scope?: AttentionScope
): BadgeCount {
  const summary = summaryFamilyCounts(data);
  if (!summary) return scopedCount(urgentAttention(data), scope);
  const total = summary.reduce((sum, entry) => sum + entry.count, 0);
  if (!scope?.family) return { count: total, elsewhere: 0 };
  const count = summary.find((entry) => entry.family === scope.family)?.count ?? 0;
  return { count, elsewhere: total - count };
}

/** One page's nav badge, for the family in scope. */
export function areaBadge(
  data: AttentionResponse | undefined,
  area: AttentionArea,
  scope?: AttentionScope
): BadgeCount {
  return scopedCount(urgentInArea(data, area), scope);
}

/** A link to Needs you, kept on one family when the caller is on one. */
export function needsYouHref(family?: string | null): string {
  const name = familyName(family);
  return name ? `${NEEDS_YOU_PATH}?family=${encodeURIComponent(name)}` : NEEDS_YOU_PATH;
}

/** A family-wide inbox link under the shared scope. */
export function blockedHref(family?: string | null): string {
  const name = familyName(family);
  return name ? `${BLOCKED_PATH}&family=${encodeURIComponent(name)}` : BLOCKED_PATH;
}

/**
 * The rows Needs you shows in red: a person is the next step. This is the one
 * derivation of that set — other surfaces read it rather than deciding for
 * themselves which of their own rows wait on someone.
 */
export function urgentAttention(data: AttentionResponse | undefined): AttentionItem[] {
  return (data?.items ?? []).filter((item) => severityOf(item) !== 'watch');
}

/**
 * The todos a person is the next step for, by id. Rows elsewhere (the In
 * flight ghosts) read membership here instead of deciding from a todo's own
 * status, so one todo is never "needs a human" on one page and calm on another.
 */
export function attentionTodoIds(data: AttentionResponse | undefined): Set<string> {
  const ids = new Set<string>();
  for (const item of urgentAttention(data)) {
    if (item.todo_id) ids.add(item.todo_id);
  }
  return ids;
}

/** `owner/name` → family, as the repository list reports it. */
export function repoFamilyMap(
  repositories: readonly { id: { owner: string; name: string }; family?: string | null }[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const repo of repositories) {
    if (repo.family) map.set(`${repo.id.owner}/${repo.id.name}`, repo.family);
  }
  return map;
}

/** Personal relevance uses canonical account logins supplied by the API. */
export function personalAttention(
  data: AttentionResponse | undefined,
  login: string | undefined
): AttentionResponse | undefined {
  if (!data) return undefined;
  const items = login
    ? data.items.filter((item) =>
        item.requested_by === login ||
        (['todo_blocked', 'todo_waiting_on_blocker', 'todo_handoff'].includes(item.kind) &&
          item.assignee === login)
      )
    : [];
  // Count the same subjects the page draws, after personal filtering.
  const byFamily = new Map<string | null, AttentionCounts>();
  for (const subject of attentionSubjects(items)) {
    const family = subject.primary.family;
    const counts = byFamily.get(family) ?? { critical: 0, action: 0, watch: 0 };
    counts[subject.severity] += 1;
    byFamily.set(family, counts);
  }
  const families = [...byFamily].map(([family, subjects]) => ({ family, subjects }));
  return { ...data, items, families };
}
